import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Dropdown } from 'semantic-ui-react';
import { Input } from '../../../lib/custom-ui';

import { useForm } from '../../../hooks';

import styles from './ItemEditStep.module.scss';

const ItemEditStep = React.memo(({ models, defaultValue, onUpdate, onClose }) => {
  const [t] = useTranslation();

  const [data, handleFieldChange] = useForm(() => ({
    ...defaultValue,
  }));

  const modelsOptions = [];

  models.forEach((modelItem) => {
    modelsOptions.push({ key: modelItem.id, value: modelItem.id, text: modelItem.name });
  });

  const dataTypesOptions = [
    { key: 0, value: 0, text: 'UINT16' },
    { key: 1, value: 1, text: 'UINT32' },
    { key: 2, value: 2, text: 'UINT64' },
    { key: 3, value: 3, text: 'INT16' },
    { key: 4, value: 4, text: 'INT32' },
    { key: 5, value: 5, text: 'INT64' },
  ];

  const readWriteOptions = [
    { key: 0, value: 0, text: t('common.itemOptions.read') },
    { key: 1, value: 1, text: t('common.itemOptions.write') },
    { key: 2, value: 2, text: t('common.itemOptions.readWrite') },
  ];

  const booleanOptions = [
    { key: 0, value: true, text: t('common.itemOptions.yes') },
    { key: 1, value: false, text: t('common.itemOptions.no') },
  ];

  const graphTypeOptions = [
    { key: 'line', value: 'line', text: t('common.components.line') },
    { key: 'bar', value: 'bar', text: t('common.components.bar') },
  ];

  let defaultOdbOptions = [];
  switch (data.dataType) {
    case 0:
    case 3:
      defaultOdbOptions = [
        { key: 0, value: 0, text: 'AB' },
        { key: 1, value: 1, text: 'BA' },
      ];
      break;
    case 1:
    case 4:
      defaultOdbOptions = [
        { key: 2, value: 2, text: 'ABCD' },
        { key: 3, value: 3, text: 'BADC' },
        { key: 4, value: 4, text: 'CDAB' },
        { key: 5, value: 5, text: 'DCBA' },
      ];
      break;
    case 2:
    case 5:
      defaultOdbOptions = [
        { key: 6, value: 6, text: 'ABCD EFGH' },
        { key: 7, value: 7, text: 'EFGH ABCD' },
        { key: 8, value: 8, text: 'CDAB GHEF' },
        { key: 9, value: 9, text: 'GHEF CDAB' },
      ];
      break;
    default:
  }
  const [orderbyOptions, setOrderbyOptions] = useState(defaultOdbOptions);

  const nameField = useRef(null);

  const [modelIdError, setModelIdError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [itemNameError, setItemNameError] = useState(false);
  const [orderbyteError, setOrderbyteError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [startBitError, setStartBitError] = useState(false);
  const [lengthBitError, setLengthBitError] = useState(false);
  const [spanHighError, setSpanHighError] = useState(false);
  const [spanLowError, setSpanLowError] = useState(false);
  const [initialValueError, setInitialValueError] = useState(false);
  const [scanRateError, setScanRateError] = useState(false);
  const [unitNameError, setUnitNameError] = useState(false);
  const [isBasicError, setIsBasicError] = useState(false);
  const [readWriteError, setReadWriteError] = useState(false);
  const [dataTypeError, setDataTypeError] = useState(false);
  const [isIntegralError, setIsIntegralError] = useState(false);
  const [isSumError, setIsSumError] = useState(false);
  const [decimalPointError, setDecimalPointError] = useState(false);
  const [minValueError, setMinValueError] = useState(false);
  const [maxValueError, setMaxValueError] = useState(false);

  const handleSubmit = useCallback(() => {
    const cleanData = {
      ...data,
      name: data.name.trim(),
      itemName: data.itemName.trim(),
      unitName: data.unitName.trim(),
    };
    let error;
    if (cleanData.modelId === '') {
      setModelIdError(true);
      error = true;
    }
    if (!cleanData.name) {
      setNameError(true);
      error = true;
    }
    if (!cleanData.itemName) {
      setItemNameError(true);
      error = true;
    }
    if (cleanData.orderbyte === '') {
      setOrderbyteError(true);
      error = true;
    }
    if (cleanData.address === '') {
      setAddressError(true);
      error = true;
    }
    if (cleanData.startBit === '') {
      setStartBitError(true);
      error = true;
    }
    if (cleanData.lengthBit === '') {
      setLengthBitError(true);
      error = true;
    }
    if (cleanData.spanHigh === '') {
      setSpanHighError(true);
      error = true;
    }
    if (cleanData.spanLow === '') {
      setSpanLowError(true);
      error = true;
    }
    if (cleanData.initialValue === '') {
      setInitialValueError(true);
      error = true;
    }
    if (cleanData.scanRate === '') {
      setScanRateError(true);
      error = true;
    }
    if (cleanData.unitName === '') {
      setUnitNameError(true);
      error = true;
    }
    if (cleanData.basic === '') {
      setIsBasicError(true);
      error = true;
    }
    if (cleanData.readWrite === '') {
      setReadWriteError(true);
      error = true;
    }
    if (cleanData.dataType === '') {
      setDataTypeError(true);
      error = true;
    }
    if (cleanData.isIntegral === '') {
      setIsIntegralError(true);
      error = true;
    }
    if (cleanData.isSum === '') {
      setIsSumError(true);
      error = true;
    }
    if (cleanData.decimalPoint === '') {
      setDecimalPointError(true);
      error = true;
    }
    if (cleanData.minValue === '') {
      setMinValueError(true);
      error = true;
    }
    if (cleanData.maxValue === '') {
      setMaxValueError(true);
      error = true;
    }
    if (error) return;
    if (cleanData !== defaultValue) {
      onUpdate(cleanData);
    }

    onClose();
  }, [defaultValue, onUpdate, onClose, data]);

  useEffect(() => {
    nameField.current.select();
  }, []);

  const handleFieldDataTypeChange = useCallback(
    (e, dataValue) => {
      handleFieldChange(e, dataValue);
      let odbOptions = [];
      switch (dataValue.value) {
        case 0:
        case 3:
          odbOptions = [
            { key: 0, value: 0, text: 'AB' },
            { key: 1, value: 1, text: 'BA' },
          ];
          break;
        case 1:
        case 4:
          odbOptions = [
            { key: 2, value: 2, text: 'ABCD' },
            { key: 3, value: 3, text: 'BADC' },
            { key: 4, value: 4, text: 'CDAB' },
            { key: 5, value: 5, text: 'DCBA' },
          ];
          break;
        case 2:
        case 5:
          odbOptions = [
            { key: 6, value: 6, text: 'ABCD EFGH' },
            { key: 7, value: 7, text: 'EFGH ABCD' },
            { key: 8, value: 8, text: 'CDAB GHEF' },
            { key: 9, value: 9, text: 'GHEF CDAB' },
          ];
          break;
        default:
      }
      setOrderbyOptions(odbOptions);
    },
    [handleFieldChange],
  );

  return (
    <>
      <Modal.Header>
        {t('common.editItem', {
          context: 'title',
        })}
      </Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <Form.Field required error={nameError} onFocus={() => setNameError(false)}>
            <label htmlFor="name">{t('common.enterItemTitle')}</label>
            <Input
              fluid
              ref={nameField}
              name="name"
              value={data.name}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={itemNameError} onFocus={() => setItemNameError(false)}>
            <label htmlFor="itemName">{t('common.enterItemName')}</label>
            <Input
              fluid
              name="itemName"
              value={data.itemName}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={modelIdError} onFocus={() => setModelIdError(false)}>
            <label htmlFor="modelId">{t('common.selectModel')}</label>
            <Dropdown
              name="modelId"
              className={styles.field}
              placeholder={t('common.selectModel')}
              search
              fluid
              selection
              value={data.modelId}
              options={modelsOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={dataTypeError} onFocus={() => setDataTypeError(false)}>
            <label htmlFor="dataType">{t('common.selectDataType')}</label>
            <Dropdown
              name="dataType"
              className={styles.field}
              placeholder={t('common.selectDataType')}
              search
              fluid
              selection
              value={data.dataType}
              options={dataTypesOptions}
              onChange={handleFieldDataTypeChange}
            />
          </Form.Field>
          <Form.Field required error={orderbyteError} onFocus={() => setOrderbyteError(false)}>
            <label htmlFor="orderbyte">{t('common.enterItemOrderbyte')}</label>
            <Dropdown
              name="orderbyte"
              className={styles.field}
              placeholder={t('common.selectOrderby')}
              search
              fluid
              selection
              value={data.orderbyte}
              options={orderbyOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={addressError} onFocus={() => setAddressError(false)}>
            <label htmlFor="address">{t('common.enterItemAddress')}</label>
            <Input
              fluid
              type="number"
              name="address"
              value={data.address}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={startBitError} onFocus={() => setStartBitError(false)}>
            <label htmlFor="startBit">{t('common.enterItemStartBit')}</label>
            <Input
              fluid
              type="number"
              name="startBit"
              value={data.startBit}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={lengthBitError} onFocus={() => setLengthBitError(false)}>
            <label htmlFor="lengthBit">{t('common.enterItemLengthBit')}</label>
            <Input
              fluid
              type="number"
              name="lengthBit"
              value={data.lengthBit}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={spanHighError} onFocus={() => setSpanHighError(false)}>
            <label htmlFor="spanHigh">{t('common.enterItemSpanHigh')}</label>
            <Input
              fluid
              type="number"
              name="spanHigh"
              value={data.spanHigh}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={spanLowError} onFocus={() => setSpanLowError(false)}>
            <label htmlFor="spanLow">{t('common.enterItemSpanLow')}</label>
            <Input
              fluid
              type="number"
              name="spanLow"
              value={data.spanLow}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field
            required
            error={initialValueError}
            onFocus={() => setInitialValueError(false)}
          >
            <label htmlFor="initialValue">{t('common.enterItemInitialValue')}</label>
            <Input
              fluid
              type="number"
              name="initialValue"
              value={data.initialValue}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={scanRateError} onFocus={() => setScanRateError(false)}>
            <label htmlFor="scanRate">{t('common.enterItemScanRate')}</label>
            <Input
              fluid
              type="number"
              name="scanRate"
              value={data.scanRate}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={unitNameError} onFocus={() => setUnitNameError(false)}>
            <label htmlFor="unitName">{t('common.enterItemUnitName')}</label>
            <Input
              fluid
              name="unitName"
              value={data.unitName}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={readWriteError} onFocus={() => setReadWriteError(false)}>
            <label htmlFor="readWrite">{t('common.selectReadWrite')}</label>
            <Dropdown
              name="readWrite"
              className={styles.field}
              placeholder={t('common.selectReadWrite')}
              search
              fluid
              selection
              value={data.readWrite}
              options={readWriteOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={isIntegralError} onFocus={() => setIsIntegralError(false)}>
            <label htmlFor="isIntegral">{t('common.selectIsIntegral')}</label>
            <Dropdown
              name="isIntegral"
              className={styles.field}
              placeholder={t('common.selectIsIntegral')}
              search
              fluid
              selection
              value={data.isIntegral}
              options={booleanOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={isSumError} onFocus={() => setIsSumError(false)}>
            <label htmlFor="isSum">{t('common.selectIsSum')}</label>
            <Dropdown
              name="isSum"
              className={styles.field}
              placeholder={t('common.selectIsSum')}
              search
              fluid
              selection
              value={data.isSum}
              options={booleanOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={isBasicError} onFocus={() => setIsBasicError(false)}>
            <label htmlFor="basic">{t('common.selectIsBasic')}</label>
            <Dropdown
              name="basic"
              className={styles.field}
              placeholder={t('common.selectIsBasic')}
              search
              fluid
              selection
              value={data.basic}
              options={booleanOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="position">{t('common.enterItemPosition')}</label>
            <Input
              fluid
              type="number"
              name="position"
              value={data.position || ''}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field
            required
            error={decimalPointError}
            onFocus={() => setDecimalPointError(false)}
          >
            <label htmlFor="decimalPoint">{t('common.enterItemDecimalPoint')}</label>
            <Input
              fluid
              type="number"
              name="decimalPoint"
              value={data.decimalPoint}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={minValueError} onFocus={() => setMinValueError(false)}>
            <label htmlFor="minValue">{t('common.enterItemMin')}</label>
            <Input
              fluid
              type="number"
              name="minValue"
              value={data.minValue}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={maxValueError} onFocus={() => setMaxValueError(false)}>
            <label htmlFor="maxValue">{t('common.enterItemMax')}</label>
            <Input
              fluid
              type="number"
              name="maxValue"
              value={data.maxValue}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="graphType">{t('common.selectGraphType')}</label>
            <Dropdown
              name="graphType"
              className={styles.field}
              placeholder={t('common.selectGraphType')}
              search
              fluid
              selection
              value={data.graphType}
              options={graphTypeOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="color">{t('common.selectColor')}</label>
            <Input
              fluid
              type="color"
              name="color"
              value={data.color || '#108775'}
              className={styles.fieldColor}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Button color="green" icon="checkmark" content={t('action.save')} floated="right" />
          <div className="clearBoth" />
        </Form>
      </Modal.Content>
    </>
  );
});

ItemEditStep.propTypes = {
  models: PropTypes.arrayOf(PropTypes.any).isRequired,
  defaultValue: PropTypes.objectOf(PropTypes.any).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default ItemEditStep;
